import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { OrganizationConfig } from '../config/tenant';
import {
  FALLBACK_CONFIG,
  DEFAULT_ORG_ID,
  getActiveOrgId,
  getFallbackConfig,
  isPlatformAdminHost,
  PLATFORM_ORG_SENTINEL,
  normalizeBranding,
} from '../config/tenant';
import { applyTenantWebAppMetadata } from '../config/tenantWebApp';

interface TenantContextType {
  config: OrganizationConfig;
  isLoading: boolean;
  organizationId: string;
}

const TenantContext = createContext<TenantContextType>({
  config: FALLBACK_CONFIG,
  isLoading: true,
  organizationId: DEFAULT_ORG_ID,
});

export const useTenant = () => useContext(TenantContext);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [organizationId] = useState(() => getActiveOrgId());
  const [config, setConfig] = useState<OrganizationConfig>(() => getFallbackConfig(organizationId));
  // Known-good branding for this tenant, used to repair anything invalid that
  // arrives from the API or a stale cache.
  const fallbackRef = useMemo(() => getFallbackConfig(organizationId), [organizationId]);
  const [isLoading, setIsLoading] = useState(true);

  // index.html and manifest.json ship with Konklave defaults; rewrite the
  // document head (title, theme color, favicon, PWA manifest) for the
  // active tenant. On the platform admin console (admin.konklave.app) the tab
  // should always represent Konklave, not whichever tenant is being managed.
  useEffect(() => {
    if (isPlatformAdminHost()) {
      applyTenantWebAppMetadata(getFallbackConfig(PLATFORM_ORG_SENTINEL), PLATFORM_ORG_SENTINEL);
    } else {
      applyTenantWebAppMetadata(config, organizationId);
    }
  }, [config, organizationId]);

  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      // Stale-while-revalidate: paint the last-known-good config immediately
      // (regardless of age) so returning visitors never see a branding flash,
      // then refresh from the API in the background below.
      const cacheKey = `tenant_config_${organizationId}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { _cachedAt, ...configData } = JSON.parse(cached);
          if (configData && configData.id) {
            const cachedConfig = configData as OrganizationConfig;
            setConfig({
              ...cachedConfig,
              branding: normalizeBranding(cachedConfig.branding, fallbackRef.branding),
            });
            setIsLoading(false);
          }
        } catch {
          /* ignore parse errors */
        }
      }

      try {
        const apiBase = process.env.REACT_APP_API_URL || 'https://api.konklave.app';
        const res = await fetch(`${apiBase}/api/organizations/${organizationId}`);
        if (!cancelled && res.ok) {
          const data = await res.json();
          // Org documents are hand-edited and have carried invalid colours
          // (e.g. `rgb108, 0, 0`), which throws inside MUI's alpha().
          const safe = {
            ...data,
            branding: normalizeBranding(data.branding, fallbackRef.branding),
          };
          setConfig(safe);
          localStorage.setItem(cacheKey, JSON.stringify({ ...safe, _cachedAt: Date.now() }));
        }
      } catch (error) {
        console.warn('Failed to load tenant config from API:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadConfig();
    return () => {
      cancelled = true;
    };
  }, [organizationId, fallbackRef]);

  return (
    <TenantContext.Provider value={{ config, isLoading, organizationId }}>
      {children}
    </TenantContext.Provider>
  );
};
